import { IFieldDef } from "@components/smart/FieldInfo";
import { ISmartFieldRenderProps } from "@components/smart/smartField/SmartField";
import BindingContext from "@odata/BindingContext";
import {
    IPrEmploymentExtraSalaryEntity,
    IPrEmploymentTemplateEntity,
    PrEmploymentTemplateEntity,
} from "@odata/GeneratedEntityTypes";
import { PrEmploymentTypeCode, PrExtraSalaryTypeCode } from "@odata/GeneratedEnums";
import React, { useCallback } from "react";

import CollapsibleSection from "../../../components/collapsibleSection/CollapsibleSection";
import SmartFastEntryList from "../../../components/smart/smartFastEntryList";
import { FormStorage } from "../../../views/formView/FormStorage";
import { isLegislativeExtraSalary } from "../employment/Employment.utils";
import { LegislativeExtraSalariesSmartFastEntryListStyled } from "./ExtraSalary.styles";

interface IProps extends ISmartFieldRenderProps {
    isCollapsible?: boolean;
    groupDefinitionId: string;
    // BindingContext to ExtraSalaryCollection
    extraSalariesBindingContext?: BindingContext;
    columns?: IFieldDef[];
}

const LegislativeExtraSalariesSmartFastEntryList: React.FC<IProps> = ({ parentProps, isCollapsible, value, groupDefinitionId, extraSalariesBindingContext, columns, ...restProps }) => {
    const storage = parentProps.storage as FormStorage<IPrEmploymentTemplateEntity>;
    const { definition, bindingContext: dataBindingContext } = storage.data;
    const { bindingContext, onChange, onBlur } = parentProps;

    const handleToggle = useCallback((isExpanded: boolean) => {
        storage.setValue(bindingContext, !isExpanded);
        storage.refreshFields(true);
    }, [storage, bindingContext]);

    const info = storage.getInfo(parentProps.bindingContext);
    const groupDef = definition.groups.find(grp => grp.id === groupDefinitionId);
    const bc = extraSalariesBindingContext ?? dataBindingContext.navigate(PrEmploymentTemplateEntity.ExtraSalaries);
    const workTypeCode = storage.getValue(bindingContext.getParent())?.TypeCode;
    const isDpcOrDpp = workTypeCode === PrEmploymentTypeCode.WorkAgreementDPC
    || workTypeCode === PrEmploymentTypeCode.WorkAgreementDPP;

    return (
            <LegislativeExtraSalariesSmartFastEntryListStyled>
                {isCollapsible && <CollapsibleSection title={info.label}
                                    onToggle={handleToggle}
                                    isExpanded={value}
                                    renderChildrenOutside={true}/>}
                {(value || !isCollapsible) && (
                        <SmartFastEntryList
                                {...restProps}
                                groupId={groupDef.id}
                                storage={storage as FormStorage}
                                bindingContext={bc}
                                useLabelWrapping
                                canAdd={false}
                                isItemRemovable={false}
                                isItemCloneable={false}
                                onChange={onChange}
                                onBlur={onBlur}
                                renderAsFastEntryForReadOnly
                                columns={columns ?? groupDef.lineItems.columns}
                                filterItems={(items: IPrEmploymentTemplateEntity[]) => {
                                     return items.filter(item => {
                                         if (!isLegislativeExtraSalary({ item })) return false;
                                         if (isDpcOrDpp) {
                                             const extraSalary = item as unknown as IPrEmploymentExtraSalaryEntity;
                                             if (extraSalary?.Default?.Type?.Code === PrExtraSalaryTypeCode.Overtime) return false;
                                         }
                                         return true;
                                     });
                                 }}
                                order={"Id"}
                        />
                )}
            </LegislativeExtraSalariesSmartFastEntryListStyled>
    );
};

export default LegislativeExtraSalariesSmartFastEntryList;